import { createSlice, isAnyOf } from '@reduxjs/toolkit';

import {
  getResonanceConcepts,
  getResonanceH2HConcepts,
  getResonanceInitData,
  getResonanceProjectInfo,
  getSingleMessageCorrelations,
  getResonanceBackgroundData,
} from './resonanceActions';
import { RESONANCE_TABS } from '../../constants';
import {
  getConsumerResonanceConcepts,
  getConsumerResonanceData,
  getConsumerResonanceH2HConcepts,
  getConsumerResonanceInitData,
  getConsumerSingleMessageCorrelations,
} from '../consumer/resonance/consumerResonanceActions';

const initialState = {
  isMessagesLoading: false,

  selectedTab: RESONANCE_TABS.MESSAGE_RESPONSE,
  projectInfo: {
    theme: '',
    vision: '',
    dataStructure: '',
    lang: '',
    projectName: '',
  },

  numberOfRespondents: 0,
  totalNodeConnections: 0,
  numberOfVariables: 0,
  numberOfResponseData: 0,
  correlationData: 0,

  filterColumns: [],
  messages: [],
  isProjectLoading: false,

  correlatedMessages: [],
  links: [],
  isHighPerformingLoading: false,

  concepts: undefined,
  conceptAverages: undefined,
  conceptsRespondents: 0,
  isConceptsLoading: false,

  conceptsH2H: [],
  isH2HConceptsLoading: false,

  benchmark: '',
  benchmarkNodes: [],

  backgroundData: [],
  isBackgroundDataLoading: false,
};

const resonanceSlice = createSlice({
  name: 'resonance',
  initialState,
  reducers: {
    selectResonanceTab: (state, action) => {
      state.selectedTab = action.payload;
    },
    saveBenchmark: (state, action) => {
      state.benchmark = action.payload.benchmark;
      state.benchmarkNodes = action.payload.benchmarkNodes;
    },

    resetResonanceProject: () => initialState,
  },
  extraReducers: (builder) => {
    builder

      .addMatcher(isAnyOf(getResonanceInitData.fulfilled, getConsumerResonanceInitData.fulfilled), (state, action) => {
        const initData = action.payload;

        state.numberOfRespondents = initData.respondentsAmount;
        state.numberOfResponseData = initData.responseDataAmount;
        state.totalNodeConnections = initData.linksAmount;
        state.numberOfVariables = initData.audiencesAmount;

        state.filterColumns = initData.filterColumns;
        state.messages = initData.messages;

        state.isProjectLoading = false;
      })
      .addMatcher(isAnyOf(getResonanceInitData.pending, getConsumerResonanceInitData.pending), (state) => {
        state.isProjectLoading = true;
      })
      .addMatcher(isAnyOf(getResonanceInitData.rejected, getConsumerResonanceInitData.rejected), (state) => {
        state.isProjectLoading = false;
      })

      .addMatcher(
        isAnyOf(getSingleMessageCorrelations.fulfilled, getConsumerSingleMessageCorrelations.fulfilled),
        (state, action) => {
          state.correlatedMessages = action.payload.correlatedMessages;
          state.links = action.payload.links;
          state.isHighPerformingLoading = false;
        },
      )
      .addMatcher(
        isAnyOf(getSingleMessageCorrelations.pending, getConsumerSingleMessageCorrelations.pending),
        (state) => {
          state.isHighPerformingLoading = true;
        },
      )
      .addMatcher(
        isAnyOf(getSingleMessageCorrelations.rejected, getConsumerSingleMessageCorrelations.rejected),
        (state) => {
          state.isHighPerformingLoading = false;
        },
      )

      .addMatcher(isAnyOf(getResonanceConcepts.fulfilled, getConsumerResonanceConcepts.fulfilled), (state, action) => {
        state.concepts = action.payload.concepts;
        state.conceptsRespondents = action.payload.respondents ?? 0;
        state.conceptAverages = action.payload.averages;
        state.isConceptsLoading = false;
      })
      .addMatcher(isAnyOf(getResonanceConcepts.pending, getConsumerResonanceConcepts.pending), (state) => {
        state.isConceptsLoading = true;
      })
      .addMatcher(isAnyOf(getResonanceConcepts.rejected, getConsumerResonanceConcepts.rejected), (state) => {
        state.isConceptsLoading = false;
      })

      .addMatcher(
        isAnyOf(getResonanceH2HConcepts.fulfilled, getConsumerResonanceH2HConcepts.fulfilled),
        (state, action) => {
          state.conceptsH2H = action.payload;
          state.isH2HConceptsLoading = false;
        },
      )
      .addMatcher(isAnyOf(getResonanceH2HConcepts.pending, getConsumerResonanceH2HConcepts.pending), (state) => {
        state.isH2HConceptsLoading = true;
      })
      .addMatcher(isAnyOf(getResonanceH2HConcepts.rejected, getConsumerResonanceH2HConcepts.rejected), (state) => {
        state.isH2HConceptsLoading = false;
      })

      .addMatcher(isAnyOf(getResonanceBackgroundData.fulfilled), (state, action) => {
        state.backgroundData = action.payload;
        state.isBackgroundDataLoading = false;
      })
      .addMatcher(isAnyOf(getResonanceBackgroundData.pending), (state) => {
        state.isBackgroundDataLoading = true;
      })
      .addMatcher(isAnyOf(getResonanceBackgroundData.rejected), (state) => {
        state.isBackgroundDataLoading = false;
      })

      .addMatcher(isAnyOf(getResonanceProjectInfo.fulfilled, getConsumerResonanceData.fulfilled), (state, action) => {
        state.projectInfo = action.payload;
      });
  },
});

export const { resetResonanceProject, selectResonanceTab, saveBenchmark } = resonanceSlice.actions;

export default resonanceSlice.reducer;
